import React from 'react';
import { StockItem } from '../slices/stocksSlice';
import './ItemDrawer.css';
import { useLang, translations } from '../i18n';

interface Props {
  item: StockItem;
  onClose: () => void;
}

const ItemDrawer: React.FC<Props> = ({ item, onClose }) => {
  const [photoIndex, setPhotoIndex] = React.useState(0);
  const [zoomed, setZoomed] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const { lang } = useLang();
  const t = translations[lang];
  const photos = item.article_sheet_obj.photos.length > 0 ? item.article_sheet_obj.photos : [process.env.PUBLIC_URL + '/photo-absente.png'];

  let used_gmc1 = undefined;
  let used_gmc2 = undefined;
  let used_gmc3 = undefined;
  let used_gmc4 = undefined;
  let used_gmc_ce = undefined;
  let used_label = undefined;

  if (lang === 'fr') {
    used_gmc1 = item.article_sheet_obj.gmc1
    used_gmc2 = item.article_sheet_obj.gmc2
    used_gmc3 = item.article_sheet_obj.gmc3
    used_gmc4 = item.article_sheet_obj.gmc4
    used_gmc_ce = item.article_sheet_obj.gmcce
    used_label = item.article_sheet_obj.label
  } else if (lang === 'en') {
    used_gmc1 = item.article_sheet_obj.gmc1_en
    used_gmc2 = item.article_sheet_obj.gmc2_en
    used_gmc3 = item.article_sheet_obj.gmc3_en
    used_gmc4 = item.article_sheet_obj.gmc4_en
    used_gmc_ce = item.article_sheet_obj.gmcce_en
    used_label = item.article_sheet_obj.label_en
  }


  const nextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i + 1) % photos.length);
  };

  const defaultOpenSpecLabel = lang === 'fr' ? 'Ouvrir la fiche technique' : 'Open spec';
  const pdfButtonLabel = pdfLoading ? (t.loadingPdf ?? '...') : (t.openSpec ?? defaultOpenSpecLabel);
  const prevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
  };

  // Helper to open a Blob in a new tab and revoke the URL after a delay
  const openBlobUrl = (blob: Blob) => {
    const blobUrl = URL.createObjectURL(blob);
    globalThis.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };
  // JSON response handler
  const handleJsonResponse = async (resp: Response) => {
    const j = await resp.json();
    let url: string | undefined;
    if (typeof j === 'string') url = j;
    else if (j && typeof j.url === 'string') url = j.url;
    if (!url) throw new Error('No URL returned');
    globalThis.open(url, '_blank', 'noopener,noreferrer');
  };

  // Text response handler
  const handleTextResponse = async (resp: Response) => {
    const txt = await resp.text();
    const turl = txt.trim();
    if (!turl) throw new Error('No URL returned');
    globalThis.open(turl, '_blank', 'noopener,noreferrer');
  };

  // Generic fetch + dispatch to proper handler
  const fetchAndHandle = async (endpoint: string) => {
    const resp = await fetch(endpoint, { method: 'GET' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const contentType = (resp.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
      await handleJsonResponse(resp);
    } else if (contentType.startsWith('text/') || contentType === '') {
      await handleTextResponse(resp);
    } else {
      // application/pdf or other binary
      const blob = await resp.blob();
      openBlobUrl(blob);
    }
  };

  // Handler extracted from inline onClick to keep render clean
  const handleOpenSpec = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (pdfLoading) return;

    const base = process.env.REACT_APP_PDF_URL;
    if (!base) {
      globalThis.alert(lang === 'fr' ? 'URL du service PDF non configurée' : 'PDF service URL not configured');
      return;
    }
    const ref = item.article_sheet_obj.article_sheet_id;
    if (!ref) {
      globalThis.alert(lang === 'fr' ? 'Référence produit introuvable' : 'Product reference not found');
      return;
    }

    try {
      setPdfLoading(true);
      const endpoint = `${base}/${lang}/${encodeURIComponent(String(ref))}`;
      await fetchAndHandle(endpoint);
    } catch (err) {
      console.error('PDF fetch error', err);
      globalThis.alert(lang === 'fr' ? 'Impossible de récupérer la fiche. Voir la console pour plus de détails.' : 'Unable to fetch the spec. See console for details.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      className="drawer-backdrop"
      aria-label={t.closeDrawer}
      onClick={onClose}
      onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') onClose(); }}
      role="button"
      tabIndex={0}
      style={{ all: 'unset', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}
    >
      <dialog open className="drawer modern-drawer" tabIndex={-1} onClick={e => e.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} aria-label={t.closeDrawer}>×</button>
        <div className="carousel-container">
          <button className="carousel-arrow" onClick={prevPhoto} aria-label={t.prevPhoto}>&#8592;</button>
          <img
            src={photos[photoIndex]}
            alt={used_label}
            className={zoomed ? 'drawer-photo zoomed' : 'drawer-photo'}
            onClick={e => { e.stopPropagation(); setZoomed(!zoomed); }}
            style={{ cursor: 'zoom-in', borderRadius: '18px', boxShadow: zoomed ? '0 0 0 4px #1976d2' : '0 2px 16px rgba(0,0,0,0.10)' }}
          />
          <button className="carousel-arrow" onClick={nextPhoto} aria-label={t.nextPhoto}>&#8594;</button>
        </div>
        <div className="carousel-indicator">
          {photos.map((photo, idx) => (
            <span key={photo} className={idx === photoIndex ? 'active' : ''}>●</span>
          ))}
        </div>
        <div style={{position:'relative', display:'flex', flexDirection:'column', alignItems:'center', marginTop: 8, marginBottom: 8}}>
          {item.article_sheet_obj.tag === 'Usage Interne' && (
            <span style={{background:'#1976d2',color:'#fff',borderRadius:12,padding:'2px 12px',fontSize:'0.92rem',fontWeight:600,boxShadow:'0 2px 8px rgba(25,118,210,0.10)',zIndex:2, alignSelf:'flex-end', marginBottom: 8}}>
              {t.usageInterne}
            </span>
          )}
          <h2 style={{ color: '#2aa04aff', textAlign:'center', margin: 0 }}>{used_label}</h2>
        </div>
        <ul className="drawer-details" style={{padding:0,margin:0,listStyle:'none',textAlign:'left'}}>
          <li><b>{t.articleSheetId} :</b> {item.article_sheet_obj.article_sheet_id ?? '-'}</li>
          <li><b>{t.label} :</b> {used_label ?? '-'}</li>
          <li><b>{t.gmcce} :</b> {used_gmc_ce ?? '-'}</li>
          <li><b>{t.gmc1} :</b> {used_gmc1 ?? '-'}</li>
          <li><b>{t.gmc2} :</b> {used_gmc2 ?? '-'}</li>
          <li><b>{t.gmc3} :</b> {used_gmc3 ?? '-'}</li>
          <li><b>{t.gmc4} :</b> {used_gmc4 ?? '-'}</li>
          <li><b>{t.longueur} :</b> {item.article_sheet_obj.longueur ?? '-'}</li>
          <li><b>{t.largeur} :</b> {item.article_sheet_obj.largeur ?? '-'}</li>
          <li><b>{t.hauteur} :</b> {item.article_sheet_obj.hauteur ?? '-'}</li>
          <li><b>{t.poids} :</b> {item.article_sheet_obj.poids ?? '-'}</li>
          <li><b>{t.itg} :</b> {item.article_sheet_obj.itg_number ?? '-'}</li>
          <li><b>{t.its} :</b> {item.article_sheet_obj.its_number ?? '-'}</li>
          <li><b>{t.pid} :</b> {item.article_sheet_obj.pid_number ?? '-'}</li>
          <li><b>{t.pdvFinal} :</b> {item.article_sheet_obj.pdv_final ?? '-'}€</li>
          <li><b>{t.pdvFiliale} :</b> {item.article_sheet_obj.pdv_filiale ?? '-'}€</li>
          <li><b>{t.manutanLink} :</b> {item.article_sheet_obj.manutan_link ? <a href={item.article_sheet_obj.manutan_link} target="_blank" rel="noopener noreferrer">{item.article_sheet_obj.manutan_link}</a> : '-'}</li>
          <li><b>{t.availableStock} :</b> {item.available_stock}</li>
          <li><b>{t.pending} :</b> {item.pending_stock}</li>
        </ul>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
          <button
            className='button-details'
            onClick={handleOpenSpec}
            disabled={pdfLoading}
            aria-busy={pdfLoading}
          >
            {pdfButtonLabel}
          </button>
        </div>
      </dialog>
      {zoomed && (
        <button className="zoomed-photo-backdrop" aria-label={t.closeZoom} onClick={e => { e.stopPropagation(); setZoomed(false); }} style={{ all: 'unset', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%'}}>
            <img src={photos[photoIndex]} alt="Zoom" className="zoomed-photo" />
          </span>
        </button>
      )}
    </div>
  );
};

export default ItemDrawer;
